// Renaming a team (RFC-1371 §6 "Manage team settings", Owner only).
//
// `PATCH /teams/{id}`, `updateTeam` and `useRenameTeam` all shipped with the
// original teams work; nothing ever called them, so a team's name was fixed at
// whatever it was created as. This is the missing control.
//
// It applies to a personal team too. That team is seeded as literally
// "Personal" by the signup trigger, which reads as a system label rather than a
// name — but it is a real, editable `teams.name` row, and now that a personal
// team can hold other people it needs to be callable something they recognise.
//
// Save is explicit rather than on-blur. The name is what teammates see in the
// switcher and on an invite preview, and a blur-triggered write turns clicking
// away mid-edit into a rename nobody asked for.

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { TeamsRequestError, type Team } from '../../api/teams'
import { useRenameTeam } from '../../hooks/useTeams'
import { cn } from '../../lib/utils'
import { ErrorText, SectionLabel, SecondaryButton } from './teamsChrome'

export default function TeamNameField({ team }: { team: Team }) {
  const { t } = useTranslation('settings')
  const rename = useRenameTeam()
  const [draft, setDraft] = useState(team.name)
  const [error, setError] = useState<string | null>(null)

  // Re-seed when the panel is pointed at a different team. Without this the
  // draft from the team you were just looking at follows you, and Save would
  // write one team's name onto another.
  useEffect(() => {
    setDraft(team.name)
    setError(null)
  }, [team.id, team.name])

  // Matches the schema's `CHECK (length(trim(name)) > 0)`, the same way
  // CreateTeamView does, so whitespace fails here rather than as a 400.
  const trimmed = draft.trim()
  const isDirty = trimmed !== team.name
  const canSubmit = trimmed.length > 0 && isDirty && !rename.isPending

  const handleSubmit = () => {
    if (!canSubmit) return
    setError(null)
    rename.mutate(
      { teamId: team.id, name: trimmed },
      {
        onError: (err) => {
          const specific = err instanceof TeamsRequestError ? err.message_ : null
          setError(specific ?? t('settingsModal.teams.renameFailed'))
        },
      },
    )
  }

  return (
    <form
      className="flex flex-col gap-2"
      data-testid="teams-rename"
      onSubmit={(event) => {
        event.preventDefault()
        handleSubmit()
      }}
    >
      <SectionLabel>{t('settingsModal.teams.teamName')}</SectionLabel>
      <div className="flex items-center gap-2">
        <input
          type="text"
          data-testid="teams-rename-input"
          aria-label={t('settingsModal.teams.teamName')}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          maxLength={80}
          autoComplete="off"
          className={cn(
            'h-9 min-w-0 flex-1 rounded-lg border border-white/15 bg-black/30 px-3 text-sm text-white',
            'placeholder:text-white/30',
            'focus:outline-none focus:ring-2 focus:ring-white/30',
          )}
        />
        <SecondaryButton type="submit" testId="teams-rename-submit" disabled={!canSubmit}>
          {t('settingsModal.teams.renameSave')}
        </SecondaryButton>
      </div>
      {error !== null && <ErrorText testId="teams-rename-error">{error}</ErrorText>}
    </form>
  )
}
